import { File as FileIcon } from 'lucide-react'
import { includes } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import iconDownload from '~/assets/icons/icon-download-blue.png'
import iconDownloadPrimary from '~/assets/icons/icon-download-dodgerblue.png'
import iconDownloadError from '~/assets/icons/icon-download-orange.png'
import { useModalState } from '~/atoms/ModalState'
import { ImageButton, Text } from '~/components/core'
import { FontStyle } from '~/components/core/Text'
import Linkify from '~/components/Linkify'
import { emojiParser, isAsciiArt } from '~/constants/parsers'
import { spacing } from '~/constants/theme'
import { useAnalytics } from '~/hooks/useAnalytics'
import { AnalyticsNamespace, MessagingAnalyticsEvents } from '~/types/Analytics'
import { openInNewTab } from '~/utils/functions'

import { Message } from '@mangovoice/types'

interface RootWrapperProps {
  $isFromTeam: boolean
}
const RootWrapper = styled.div<RootWrapperProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: ${({ $isFromTeam }) =>
    $isFromTeam ? 'flex-end' : 'flex-start'};
`

interface BubbleWrapperProps {
  $isFromTeam: boolean
  $borderStyle: string
  $enlargeEmojies: boolean
  $color: string
}
const BubbleWrapper = styled.div<BubbleWrapperProps>`
  word-break: break-word;
  max-width: 55%;
  border: ${(props) => props.$borderStyle};
  padding: ${spacing.spacingMD};
  padding-left: ${spacing.spacingLG};
  padding-right: ${spacing.spacingLG};
  border-top-right-radius: ${spacing.spacingXL};
  border-bottom-left-radius: ${spacing.spacingXL};
  border-top-left-radius: ${({ $isFromTeam }) =>
    $isFromTeam ? `${spacing.spacingXL}` : '0px'};
  border-bottom-right-radius: ${({ $isFromTeam }) =>
    $isFromTeam ? '0px' : `${spacing.spacingXL}`};
  box-sizing: border-box;
  background-color: ${(props) => props.$color};
`

const BubbleImageWrapper = styled.div`
  min-height: 25px;
  position: relative;
`

const BubbleImagePreviewButton = styled.button`
  position: relative;
  display: block;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 5px;
  background: none;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 5px;
    background: rgba(0, 0, 0, 0.2);
    opacity: 0;
  }

  ${BubbleImageWrapper}:hover &::after,
  ${BubbleImageWrapper}:focus-within &::after {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--chakra-colors-brand-fg);
    outline-offset: 2px;
  }
`

interface BubbleDownloadWrapperProps {
  $messageDirection: string
}
const BubbleDownloadWrapper = styled.div<BubbleDownloadWrapperProps>`
  position: absolute;
  ${({ $messageDirection }) =>
    $messageDirection === 'outbound' ? 'left: -20px;' : 'right: -20px;'};
  top: 50%;
  transform: translateY(-50%);
  background: var(--chakra-colors-bg-panel);
  padding: 10px;
  border-radius: 20px;
  border: 1px solid var(--chakra-colors-border-default);
  opacity: 0;

  ${BubbleImageWrapper}:hover &,
  ${BubbleImageWrapper}:focus-within & {
    opacity: 1;
  }
`

const BubbleImage = styled.img`
  max-height: 150px;
  width: auto;
  max-width: 100%;
  border-radius: 5px;
`

const DownloadWrapper = styled.div`
  width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

const BubbleFileWrapper = styled.div`
  height: 56px;
  display: flex;
  align-items: center;

  &:hover ${DownloadWrapper}, &:focus-within ${DownloadWrapper} {
    width: 56px;
  }
`

const BubbleFileDetailWrapper = styled.div`
  width: 144px;
  height: 100%;
  padding: 10px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  background-color: var(--chakra-colors-border-default);

  > p {
    margin-left: 10px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

interface MessageBubbleProps {
  message: Message
  fromCurrentUser: boolean
  isFromTeam: boolean
  senderName?: string
  overrideMaxWidth?: string
}

const MessageBubble = ({
  message,
  fromCurrentUser,
  isFromTeam,
  senderName,
  overrideMaxWidth,
}: MessageBubbleProps) => {
  const { t } = useTranslation()
  const {
    previewModal: [, setPreviewModalState],
  } = useModalState()
  const { track } = useAnalytics(AnalyticsNamespace.MESSAGING)
  const handleOpenPreview = (url: string, filename: string) => () => {
    track(MessagingAnalyticsEvents.MmsMessagePreviewed)
    setPreviewModalState({
      open: true,
      previewUrl: url,
      previewFilename: filename,
    })
  }

  const handleClickShare = (url: string) => () => {
    openInNewTab(url)
  }

  const handleDownload = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    url: string,
    filename: string,
  ) => {
    event.stopPropagation()
    track(MessagingAnalyticsEvents.MmsMessageDownloaded)

    const img = await fetch(url, {
      method: 'GET',
      headers: { 'Cache-Control': 'no-cache' },
    })
    const blob = await img.blob()
    const imgUrl = URL.createObjectURL(blob)

    const anchor = document.createElement('a')
    anchor.href = imgUrl
    anchor.download = filename.replace('.tif', '.pdf')

    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)

    URL.revokeObjectURL(imgUrl)
  }

  const { body, files } = message

  // Count of emojis for the current message.
  const { enlargeEmojies } = emojiParser(body)
  const textIsAsciiArt = isAsciiArt(body)

  // Assign the corresponding color to the bubble.
  let bubbleColor = 'var(--chakra-colors-bg-default)'
  let textColor = 'var(--chakra-colors-fg-default)'
  let borderStyle = '1.5px solid var(--chakra-colors-border-default)'
  if (enlargeEmojies) {
    bubbleColor = 'var(--chakra-colors-transparent)'
    borderStyle = '0px solid transparent'
  } else if (isFromTeam) {
    bubbleColor = 'var(--chakra-colors-brand-subtle)'
    textColor = 'var(--chakra-colors-brand-fg)'
  }

  return (
    <RootWrapper $isFromTeam={isFromTeam}>
      <>
        {body && body !== '' && (
          <BubbleWrapper
            style={{
              maxWidth: overrideMaxWidth ? `${overrideMaxWidth}` : 'auto',
            }}
            $enlargeEmojies={enlargeEmojies}
            $isFromTeam={isFromTeam}
            $color={bubbleColor}
            $borderStyle={borderStyle}
          >
            <Linkify>
              <Text
                fontStyle={FontStyle.Small}
                color={textColor}
                style={
                  enlargeEmojies
                    ? {
                        fontSize: '42px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                      }
                    : {
                        fontFamily: textIsAsciiArt ? 'monospace' : 'inherit',
                        fontSize: '12px',
                        whiteSpace: 'pre-wrap',
                      }
                }
              >
                {body}
              </Text>
            </Linkify>
          </BubbleWrapper>
        )}
        {files.map(({ name, mime_type, url }) => {
          const isImage = includes('image/', mime_type)
          if (isImage) {
            return (
              <BubbleImageWrapper key={`image-${name}-${url}`}>
                <BubbleImagePreviewButton
                  type="button"
                  aria-label={t('a11y.messages.openImage', { name })}
                  onClick={handleOpenPreview(url, name)}
                  onContextMenu={(e) => {
                    e.preventDefault()
                  }}
                >
                  <BubbleImage
                    src={url}
                    alt={
                      name ||
                      t('a11y.messages.imageFrom', {
                        sender: senderName ?? '',
                      })
                    }
                  />
                </BubbleImagePreviewButton>
                <BubbleDownloadWrapper $messageDirection={message.direction}>
                  <ImageButton
                    aria-label={t('a11y.messages.downloadFile', { name })}
                    icon={iconDownload}
                    width="100%"
                    height="20px"
                    imgWidth={20}
                    imgHeight={20}
                    backColor="var(--chakra-colors-transparent)"
                    hoveredColor="var(--chakra-colors-transparent)"
                    activeColor="var(--chakra-colors-transparent)"
                    onClick={(e) => handleDownload(e, url, name)}
                  />
                </BubbleDownloadWrapper>
              </BubbleImageWrapper>
            )
          }
          return (
            <BubbleFileWrapper key={`image-${name}-${url}`}>
              <DownloadWrapper>
                <ImageButton
                  aria-label={t('a11y.messages.downloadFile', { name })}
                  icon={iconDownloadPrimary}
                  hoverIcon={iconDownloadError}
                  activeIcon={iconDownload}
                  width="40px"
                  height="40px"
                  imgWidth={25}
                  imgHeight={20}
                  backColor="var(--chakra-colors-transparent)"
                  hoveredColor="var(--chakra-colors-transparent)"
                  activeColor="var(--chakra-colors-transparent)"
                  onClick={handleClickShare(url)}
                />
              </DownloadWrapper>
              <BubbleFileDetailWrapper>
                <FileIcon size={30} color="var(--chakra-colors-fg-muted)" />
                <Text
                  fontStyle={FontStyle.ExTiny}
                  text={name}
                  color={
                    fromCurrentUser
                      ? 'var(--chakra-colors-brand-contrast)'
                      : 'var(--chakra-colors-fg-default)'
                  }
                />
              </BubbleFileDetailWrapper>
            </BubbleFileWrapper>
          )
        })}
      </>
    </RootWrapper>
  )
}

export default MessageBubble
